"use client";

import React, { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Logo } from "@/components/brand/logo";
import { Bell, User, Settings, LogOut, Menu, Sun, Moon, Building2, Key } from "lucide-react";
import { useSession } from "next-auth/react";
import { fullLogout } from "@/lib/auth-utils";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { CompanySelector } from "@/components/layout/company-selector";
import { useCurrentCompany } from "@/hooks/api/use-companies";
import type { Session } from "next-auth";
import { ChangePasswordSimpleModal } from "@/components/auth/change-password-simple-modal";

interface NavbarProps {
  onMenuClick?: () => void;
  sidebarOpen?: boolean;
}

export function Navbar({ onMenuClick, sidebarOpen = true }: NavbarProps) {
  const { data: session } = useSession();
  const userInitials = session?.user?.name
    ?.split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2) || "U";

  return (
    <header
      className={cn(
        "fixed top-0 right-0 z-50 h-16 border-b backdrop-blur supports-[backdrop-filter]:bg-opacity-60 transition-all duration-300",
        sidebarOpen ? "left-0 lg:left-64" : "left-0 lg:left-0"
      )}
      style={{
        borderColor: "var(--p-border-color)",
        background: "rgba(var(--p-surface-0-rgb, 255, 255, 255), 0.95)",
      }}
    >
      <div className="flex h-full items-center gap-4 px-4 lg:px-6">
        {/* Left - Menu Button (Mobile & Desktop) */}
        <div className="flex items-center gap-3">
          <button
            type="button"
            className="-ml-2 flex items-center justify-center h-9 w-9 rounded-md transition-colors"
            style={{ color: "var(--p-text-muted-color)" }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = "var(--p-surface-50)";
              e.currentTarget.style.color = "var(--p-text-color)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = "transparent";
              e.currentTarget.style.color = "var(--p-text-muted-color)";
            }}
            onClick={onMenuClick}
            aria-label={sidebarOpen ? "Fechar menu" : "Abrir menu"}
          >
            <Menu className="h-5 w-5 transition-transform duration-200" />
          </button>
          <div className="lg:hidden">
            <Logo width={110} height={36} />
          </div>
        </div>

        {/* Right - Actions */}
        <div className="flex items-center gap-2 ml-auto">

          {/* Company Selector */}
          <CompanySelector />

          {/* Notifications Button */}
          <Button
            variant="ghost"
            size="icon"
            className="relative h-9 w-9"
            aria-label="Notificações - Você tem novas notificações"
            style={{ color: "var(--p-text-muted-color)" }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = "var(--p-surface-50)";
              e.currentTarget.style.color = "var(--p-text-color)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = "transparent";
              e.currentTarget.style.color = "var(--p-text-muted-color)";
            }}
          >
            <Bell className="h-5 w-5" aria-hidden="true" />
            <span className="absolute right-1.5 top-1.5 flex h-2 w-2" aria-hidden="true">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-error opacity-75" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-error" />
            </span>
          </Button>

          {/* Divider */}
          <div className="hidden h-6 w-px lg:block" style={{ backgroundColor: "var(--p-border-color)" }} />

          {/* User Menu */}
          <UserMenu userInitials={userInitials} session={session} />
        </div>
      </div>
    </header>
  );
}

function UserMenu({ userInitials, session }: { userInitials: string; session: Session | null }) {
  const [theme, setTheme] = useState<"topamais" | "dark">("topamais");
  const [isChangePasswordModalOpen, setIsChangePasswordModalOpen] = useState(false);
  const router = useRouter();

  // Busca a empresa atual
  const currentCompanyQuery = useCurrentCompany();

  useEffect(() => {
    const saved = (typeof window !== "undefined" && localStorage.getItem("theme")) || undefined;
    const initial = (saved as "topamais" | "dark") || (document.documentElement.getAttribute("data-theme") as "topamais" | "dark" | null) || "topamais";
    setTheme(initial);
  }, []);

  useEffect(() => {
    if (typeof document === "undefined") return;
    document.documentElement.setAttribute("data-theme", theme);
  }, [theme]);

  const toggleTheme = () => {
    const next = theme === "dark" ? "topamais" : "dark";
    setTheme(next);
    if (typeof window !== "undefined") {
      localStorage.setItem("theme", next);
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button 
          className="flex items-center justify-center rounded-full p-0 transition-colors hover:opacity-80 focus:outline-none focus:ring-2 focus:ring-offset-2" 
          style={{ 
            color: "var(--p-text-color)",
            width: "2.5rem",
            height: "2.5rem",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = "var(--p-surface-50)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = "transparent";
          }}
          aria-label="Menu do usuário"
          type="button"
        >
          {session?.user?.image ? (
            <AvatarImage 
              src={session.user.image} 
              alt={session?.user?.name || "Usuário"}
              className="h-10 w-10"
            />
          ) : (
            <AvatarFallback className="h-10 w-10 text-sm font-medium">
              {userInitials}
            </AvatarFallback>
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="w-64" align="end">
        <li
          className="px-3 py-3 border-b list-none"
          style={{ borderColor: "var(--p-border-color)", cursor: "default" }}
        >
          <div className="flex items-center gap-3">
            {session?.user?.image ? (
              <AvatarImage 
                src={session.user.image} 
                alt={session?.user?.name || "Usuário"}
                className="h-12 w-12 flex-shrink-0"
              />
            ) : (
              <AvatarFallback className="h-12 w-12 text-sm font-medium flex-shrink-0">
                {userInitials}
              </AvatarFallback>
            )}
            <div className="flex-1 min-w-0">
              <p className="truncate text-sm font-medium" style={{ color: "var(--p-text-color)" }}>
                {session?.user?.name || "Usuário"}
              </p>
              <p className="truncate text-xs" style={{ color: "var(--p-text-muted-color)" }}>
                {session?.user?.email}
              </p>
              {currentCompanyQuery.data?.name && (
                <div className="flex items-center gap-1.5 mt-1.5">
                  <Building2 className="h-3 w-3 flex-shrink-0" style={{ color: "var(--p-text-muted-color)" }} />
                  <p className="truncate text-xs" style={{ color: "var(--p-text-muted-color)" }}>
                    {currentCompanyQuery.data.name}
                  </p>
                </div>
              )}
            </div>
          </div>
        </li>
        <DropdownMenuItem 
          className="cursor-pointer"
          onClick={() => router.push("/configuracoes/perfil")}
        >
          <div className="flex items-center gap-2">
            <User className="h-4 w-4" style={{ color: "var(--p-text-muted-color)" }} />
            <span style={{ color: "var(--p-text-color)" }}>Meu Perfil</span>
          </div>
        </DropdownMenuItem>
        <DropdownMenuItem 
          className="cursor-pointer"
          onClick={() => router.push("/configuracoes")}
        >
          <div className="flex items-center gap-2">
            <Settings className="h-4 w-4" style={{ color: "var(--p-text-muted-color)" }} />
            <span style={{ color: "var(--p-text-color)" }}>Configurações</span>
          </div>
        </DropdownMenuItem>
        <DropdownMenuItem 
          className="cursor-pointer"
          onClick={() => setIsChangePasswordModalOpen(true)}
        >
          <div className="flex items-center gap-2">
            <Key className="h-4 w-4" style={{ color: "var(--p-text-muted-color)" }} />
            <span style={{ color: "var(--p-text-color)" }}>Alterar Senha</span>
          </div>
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem className="cursor-pointer" onClick={toggleTheme}>
          <div className="flex items-center gap-2">
            {theme === "dark" ? (
              <Sun className="h-4 w-4" style={{ color: "var(--p-text-muted-color)" }} />
            ) : (
              <Moon className="h-4 w-4" style={{ color: "var(--p-text-muted-color)" }} />
            )}
            <span style={{ color: "var(--p-text-color)" }}>Tema: {theme === "dark" ? "Claro" : "Escuro"}</span>
          </div>
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onClick={() => fullLogout()}
          className="cursor-pointer"
          style={{ 
            color: "var(--p-red-600, #dc2626)",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = "var(--p-red-50, rgba(254, 242, 242, 1))";
            e.currentTarget.style.color = "var(--p-red-700, #b91c1c)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = "transparent";
            e.currentTarget.style.color = "var(--p-red-600, #dc2626)";
          }}
        >
          <div className="flex items-center gap-2">
            <LogOut className="h-4 w-4" />
            <span>Sair</span>
          </div>
        </DropdownMenuItem>
      </DropdownMenuContent>
      <ChangePasswordSimpleModal
        visible={isChangePasswordModalOpen}
        onHide={() => setIsChangePasswordModalOpen(false)}
      />
    </DropdownMenu>
  );
}
